import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { token, user } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const hasEverConnectedRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const listenersRef = useRef(new Map());

  // Get WebSocket URL from backend URL
  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    // Convert http/https to ws/wss
    const wsProtocol = backendUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = backendUrl.replace(/^https?:\/\//, "");
    return `${wsProtocol}://${wsHost}/ws/${token}`;
  }, [token]);

  // Handle incoming messages
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      setLastMessage(message);

      // Handle different message types
      switch (message.type) {
        case "connected":
          console.log("WebSocket connected:", message.message);
          break;
          
        case "video_call_started":
          toast.success(
            `Incoming video call! ${message.data.therapist_name} started a call for ${message.data.patient_name}`,
            { duration: 15000 }
          );
          break;
          
        case "video_call_ended":
          toast.info(
            `Video call ended for ${message.data.patient_name || 'session'}`,
            { duration: 5000 }
          );
          break;
          
        case "patient_checked_in":
          toast.info(
            `Patient ${message.data.patient_name} checked in`,
            { duration: 5000 }
          );
          break;
          
        case "patient_ready":
          // Only show toast if this notification is targeted at the current user
          // The backend sends assigned_therapist_user_id in the notification data
          // If it's present and doesn't match current user, skip the toast
          if (message.data.assigned_therapist_user_id && 
              user?.id && 
              message.data.assigned_therapist_user_id !== user.id) {
            console.log("Ignoring patient_ready notification - not assigned to current user");
            break;
          }
          toast.info(
            `Patient ${message.data.patient_name} is ready (${message.data.discipline})`,
            { duration: 5000 }
          );
          break;
          
        case "patient_ready_unassigned":
          // This is shown to facilitators/admins when a patient is ready but has no assigned therapist
          // This shouldn't happen with proper booking flow, but notify staff to investigate
          toast.warning(
            `Alert: ${message.data.patient_name} is ready but no therapist assigned!`,
            { duration: 10000 }
          );
          break;
          
        case "session_started":
          toast.info(
            `Session started: ${message.data.patient_name} with ${message.data.therapist_name}`,
            { duration: 5000 }
          );
          break;
          
        case "session_completed":
          toast.success(
            `Session completed: ${message.data.patient_name}`,
            { duration: 5000 }
          );
          break;
          
        case "ping":
          // Respond to server pings
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "pong" }));
          }
          break;
          
        default:
          // Notify all listeners for custom handling
          listenersRef.current.forEach((callback) => {
            callback(message);
          });
      }
    } catch (error) {
      console.log("WebSocket message (non-JSON):", event.data);
    }
  }, []);

  // Subscribe to specific message types
  const subscribe = useCallback((messageType, callback) => {
    const id = `${messageType}-${Date.now()}`;
    listenersRef.current.set(id, callback);
    return () => {
      listenersRef.current.delete(id);
    };
  }, []);

  // Send message through WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof message === "string" ? message : JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, "User logged out");
      wsRef.current = null;
    }
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Connect/reconnect to WebSocket
  useEffect(() => {
    if (!token || !user) {
      disconnect();
      return;
    }

    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const connectWebSocket = () => {
      try {
        const wsUrl = getWsUrl();
        console.log("Connecting to WebSocket:", wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log("WebSocket connected");
          setIsConnected(true);
          setConnectionFailed(false);
          hasEverConnectedRef.current = true;
          reconnectAttemptsRef.current = 0;
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        };

        ws.onmessage = handleMessage;

        ws.onerror = (error) => {
          // Don't log error details - just note it failed
          console.log("WebSocket connection issue");
        };

        ws.onclose = (event) => {
          console.log("WebSocket closed:", event.code);
          setIsConnected(false);
          wsRef.current = null;

          // Only attempt reconnect if we've successfully connected before
          // and this wasn't an intentional close
          if (event.code !== 1000 && hasEverConnectedRef.current && reconnectAttemptsRef.current < 3) {
            const delay = 15000; // 15 seconds between attempts
            reconnectAttemptsRef.current += 1;
            console.log(`Reconnecting in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current})...`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket();
            }, delay);
          } else if (!hasEverConnectedRef.current) {
            // Never connected - WebSocket likely not available
            setConnectionFailed(true);
            console.log("WebSocket not available - using polling for updates");
          }
        };
      } catch (error) {
        console.log("WebSocket setup failed - using polling");
        setConnectionFailed(true);
      }
    };

    connectWebSocket();

    return () => {
      disconnect();
    };
  }, [token, user, getWsUrl, handleMessage, disconnect]);

  // Ping to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send("ping");
      }
    }, 25000);

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  const value = {
    isConnected,
    connectionFailed, // True if WebSocket is not available at all
    lastMessage,
    subscribe,
    sendMessage,
    disconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
